import { useCallback, useState } from 'react';
import { Box, Menu, MenuItem, Tooltip } from '@mui/material';

import { TableRow, TableCell, IconButton, Divider, MoreVertIcon } from '@occc-app/design-system';
import { RetailerNetworkLocationFragment } from '@occc/graphql';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import {
  RemoveLocationsFromRetailerNetworkLocationGroup,
  UpdateRetailerNetworkLocations,
} from '@occc-app/App/PolicyBasedAccessControl/userActions';

import { ConnectionStatusChip } from './retailer-network-group-connection-status-chip.component';

import {
  getLocationAddress,
  getLocationPhoneNumber,
  getLocationSiteId,
  getLocationSiteName,
} from '../retailer-network-my-stores-group-v2-page.utils';

const truncatedSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

type GroupLocationsTableRowProps = {
  location: RetailerNetworkLocationFragment;
  retailerNetworkUuid: string;
  onViewStoreDetails: (location: RetailerNetworkLocationFragment) => void;
  onEditStore: (location: RetailerNetworkLocationFragment) => void;
  onRemoveLocation: (locationUuid: string) => void;
};

export const GroupLocationsTableRow = ({
  location,
  retailerNetworkUuid,
  onViewStoreDetails,
  onEditStore,
  onRemoveLocation,
}: GroupLocationsTableRowProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
  }, []);

  const handleMenuItem = (action: () => void) => (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    action();
    setAnchorEl(null);
  };

  const siteId = getLocationSiteId(location);
  const siteName = getLocationSiteName(location);
  const address = getLocationAddress(location);
  const phone = getLocationPhoneNumber(location);

  return (
    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => onViewStoreDetails(location)}>
      <TableCell sx={{ overflow: 'hidden' }}>
        <Tooltip title={siteId} placement='top-start'>
          <Box sx={truncatedSx}>{siteId}</Box>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ overflow: 'hidden' }}>
        <Tooltip title={siteName} placement='top-start'>
          <Box sx={truncatedSx}>{siteName}</Box>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ overflow: 'hidden' }}>
        <Tooltip title={address} placement='top-start'>
          <Box sx={truncatedSx}>{address}</Box>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ overflow: 'hidden' }}>
        <Tooltip title={phone} placement='top-start'>
          <Box sx={truncatedSx}>{phone}</Box>
        </Tooltip>
      </TableCell>
      <TableCell>
        {/* TODO(OCST-17349): pass real status once per-store online status is exposed on the query. */}
        <ConnectionStatusChip />
      </TableCell>
      <TableCell align='right'>
        <IconButton onClick={openMenu} sx={{ p: 0 }} data-testid='rc-group-row-actions'>
          <MoreVertIcon />
        </IconButton>
        <Menu
          open={isMenuOpen}
          onClose={closeMenu}
          anchorEl={anchorEl}
          PaperProps={{ sx: { borderRadius: '4px' } }}
        >
          <MenuItem onClick={handleMenuItem(() => onViewStoreDetails(location))}>
            View Store Details
          </MenuItem>
          <Can I={UpdateRetailerNetworkLocations({ uuid: retailerNetworkUuid })}>
            <MenuItem onClick={handleMenuItem(() => onEditStore(location))}>Edit Store</MenuItem>
          </Can>
          <Can I={RemoveLocationsFromRetailerNetworkLocationGroup({ uuid: retailerNetworkUuid })}>
            <Divider />
            <MenuItem onClick={handleMenuItem(() => onRemoveLocation(location.location.uuid))}>
              Remove Location From Location Group
            </MenuItem>
          </Can>
        </Menu>
      </TableCell>
    </TableRow>
  );
};
